import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Sparkles, Users, Shield, Award, Brain } from 'lucide-react';
import { useSchema } from '../hooks/useSchema';
import { useAuth } from '../contexts/AuthContext';
import PublicHeader from '../components/PublicHeader';

const About = () => {
  const { isAuthenticated } = useAuth();
  
  // SEO Schema - Organization
  useSchema({
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "CoRegulate",
      "description": "ADHD-friendly emotional regulation app created by a Clinical Hypnotherapist",
      "url": "https://coregulateadhd.com",
      "foundingDate": "2024",
      "slogan": "Made for ADHD hearts, shaped by ADHD minds"
    }
  });

  return (
    <>
      <Helmet>
        <title>About CoRegulate | ADHD Emotional Regulation App</title>
        <meta name="description" content="Learn about CoRegulate - an ADHD emotional regulation app created by a Clinical Hypnotherapist. Our mission is to help neurodivergent minds find calm and connection." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://coregulateadhd.com/about" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About CoRegulate | ADHD Emotional Regulation" />
        <meta property="og:description" content="Learn about CoRegulate - an ADHD emotional regulation app created by a Clinical Hypnotherapist." />
        <meta property="og:url" content="https://coregulateadhd.com/about" />
        <meta property="og:site_name" content="CoRegulate" />
        <meta property="og:image" content="https://coregulateadhd.com/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About CoRegulate | ADHD Emotional Regulation" />
        <meta name="twitter:description" content="Learn about CoRegulate - an ADHD emotional regulation app created by a Clinical Hypnotherapist." />
        <meta name="twitter:image" content="https://coregulateadhd.com/og-image.png" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4]">
        {/* Header - Matching site style */}
        <PublicHeader currentPage="about" />

        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-16 md:py-24 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 bg-[#1D6B6B]/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Heart className="w-10 h-10 text-[#1D6B6B]" />
              </div>
              <h1 className="text-4xl md:text-5xl font-serif text-[#1D3D3D] mb-6">
                About <span className="text-[#1D6B6B]">CoRegulate</span>
              </h1>
              <p className="text-lg md:text-xl text-[#5A7A7A] max-w-2xl mx-auto">
                Made for ADHD hearts, shaped by ADHD minds. We're building the emotional 
                regulation toolkit we wished we had.
              </p>
            </div>
          </section>

          {/* Mission Section */}
          <section className="py-16 px-4 bg-white/50">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] mb-4">
                    Our Mission
                  </h2>
                  <p className="text-[#5A7A7A] leading-relaxed mb-4">
                    Living with ADHD means navigating a world that wasn't designed for how our minds work. 
                    Emotional dysregulation — those intense feelings that seem to come out of nowhere — 
                    is one of the most challenging aspects.
                  </p>
                  <p className="text-[#5A7A7A] leading-relaxed mb-4">
                    We created CoRegulate to be the gentle companion we needed: something that meets us 
                    in the overwhelm, guides us back to calm, and helps us understand ourselves better 
                    over time.
                  </p>
                  <p className="text-[#5A7A7A] leading-relaxed">
                    Our mission is to make emotional regulation accessible, stigma-free, and actually 
                    usable for neurodivergent minds.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-8 border border-[#E8E1D6] shadow-sm">
                  <blockquote className="text-lg italic text-[#1D3D3D]">
                    "You're already doing something brave by being here. Whether you're feeling 
                    overwhelmed, disconnected, or just curious — this is a safe space to explore 
                    at your own pace."
                  </blockquote>
                  <p className="text-[#7D8E95] mt-4">— The CoRegulate Philosophy</p>
                </div>
              </div>
            </div>
          </section>

          {/* Creator Section */}
          <section className="py-16 px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] text-center mb-12">
                Created by Professionals Who Understand
              </h2>
              <div className="bg-white rounded-2xl p-8 border border-[#E8E1D6] shadow-sm">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#1D6B6B]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-[#1D6B6B]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1D3D3D] mb-2">
                      Clinical Hypnotherapist Designed
                    </h3>
                    <p className="text-[#5A7A7A] leading-relaxed">
                      CoRegulate was created and designed by a Clinical Hypnotherapist with deep expertise 
                      in emotional regulation, nervous system work, and ADHD. Every tool, every guided 
                      meditation, and every technique is grounded in therapeutic practice and tailored 
                      for neurodivergent needs.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1D6B6B]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Brain className="w-6 h-6 text-[#1D6B6B]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#1D3D3D] mb-2">
                      Evidence-Based Approaches
                    </h3>
                    <p className="text-[#5A7A7A] leading-relaxed">
                      Our tools incorporate techniques from polyvagal theory, somatic experiencing, 
                      cognitive behavioral therapy, and mindfulness — all adapted to be accessible 
                      and effective for ADHD minds.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Values Section */}
          <section className="py-16 px-4 bg-white/50">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] text-center mb-12">
                Our Values
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl p-6 border border-[#E8E1D6] text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 bg-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-7 h-7 text-rose-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1D3D3D] mb-2">Compassion First</h3>
                  <p className="text-sm text-[#5A7A7A]">
                    No judgment, no pressure. We meet you exactly where you are with kindness and understanding.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-[#E8E1D6] text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-7 h-7 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1D3D3D] mb-2">ADHD-Centric Design</h3>
                  <p className="text-sm text-[#5A7A7A]">
                    Short exercises, clear guidance, and minimal friction. Built for how our minds actually work.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-[#E8E1D6] text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1D3D3D] mb-2">Privacy & Safety</h3>
                  <p className="text-sm text-[#5A7A7A]">
                    Your emotional journey is private. We protect your data and create a safe space to explore.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Community Section */}
          <section className="py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-16 h-16 bg-[#1D6B6B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[#1D6B6B]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] mb-4">
                A Growing Community
              </h2>
              <p className="text-[#5A7A7A] max-w-2xl mx-auto mb-8">
                Thousands of people with ADHD are using CoRegulate to build emotional resilience, 
                improve their relationships, and develop a kinder relationship with themselves. 
                You're not alone on this journey.
              </p>
              <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
                <div className="bg-white rounded-xl p-4 border border-[#E8E1D6] shadow-sm">
                  <div className="text-2xl font-bold text-[#1D6B6B]">5k+</div>
                  <div className="text-xs text-[#7D8E95]">Active Users</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-[#E8E1D6] shadow-sm">
                  <div className="text-2xl font-bold text-[#1D6B6B]">50+</div>
                  <div className="text-xs text-[#7D8E95]">Regulation Tools</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-[#E8E1D6] shadow-sm">
                  <div className="text-2xl font-bold text-[#1D6B6B]">24/7</div>
                  <div className="text-xs text-[#7D8E95]">AI Support</div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 md:py-24 px-4 bg-white/50">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] mb-4">
                Ready to Join Us?
              </h2>
              <p className="text-[#5A7A7A] mb-8">
                Start your emotional regulation journey today. No pressure, no judgment — 
                just gentle guidance at your own pace.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/auth" 
                  className="inline-flex items-center justify-center gap-2 text-white px-8 py-3 rounded-full font-medium hover:opacity-90 transition-all shadow-md"
                  style={{
                      background: 'linear-gradient(135deg, #7D8E95 0%, #2A8585 35%, #1D6B6B 65%, #FFBB98 100%)'
                  }}
                >
                  Start for Free
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link 
                  to="/how-it-works" 
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#1D3D3D] px-8 py-3 rounded-full font-medium border border-[#E8E1D6] hover:bg-white/70 transition-colors"
                >
                  See How It Works
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-white/80 border-t border-[#E8E1D6]">
          <div className="max-w-6xl mx-auto px-4 py-8 text-center space-x-6">
            <Link to="/how-it-works" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              How It Works
            </Link>
            <Link to="/tools" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              Tools
            </Link>
            <Link to="/contact" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              Contact
            </Link>
            <Link to="/privacy" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              Privacy
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
};

export default About;
